/**
 * iter 73.15 Phase H — Map Design section.
 *
 * Super-admin surface inside Settings > My Organization. Lets an org
 * customize how the Command Center map renders their taxonomy:
 *
 *   • statuses[]   → color + icon per job_status
 *   • priorities[] → color + icon per location_priority
 *   • tags[]       → color + icon per location tag
 *   • meeting      → color + icon for is_setup_meeting pins (single override)
 *   • default      → fallback color + icon for non-classified pins
 *
 * The whole blob is stored on `orgs.map_settings` (JSONB) and consumed
 * by `pages/CommandCenter.jsx` on the map render pass.
 *
 * IMPORTANT: The mobile driver view intentionally IGNORES map_settings
 * (fixed high-contrast safety palette). Do not attempt to consume these
 * settings on the Strike 3 mobile app.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Palette, MapPin, Star, Flag, Diamond, Handshake, Circle, Square,
  FloppyDisk, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

// Preset icon library. The KEY is stored in map_settings; the Command
// Center map converts it to an SVG symbol at render time. Keep this
// list small — dispatchers don't need 50 icons.
export const MAP_ICON_LIBRARY = [
  { key: "pin",       label: "Pin",       Icon: MapPin },
  { key: "circle",    label: "Circle",    Icon: Circle },
  { key: "square",    label: "Square",    Icon: Square },
  { key: "diamond",   label: "Diamond",   Icon: Diamond },
  { key: "star",      label: "Star",      Icon: Star },
  { key: "flag",      label: "Flag",      Icon: Flag },
  { key: "handshake", label: "Meeting",   Icon: Handshake },
];

const DEFAULT_PALETTE = [
  "#3b82f6", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316",
  "#eab308", "#10b981", "#14b8a6", "#06b6d4", "#6b7280",
];

function IconPicker({ value, onChange }) {
  return (
    <select
      value={value || "circle"}
      onChange={(e) => onChange(e.target.value)}
      className="input text-xs"
      data-testid="mapdesign-icon-picker"
    >
      {MAP_ICON_LIBRARY.map((i) => (
        <option key={i.key} value={i.key}>{i.label}</option>
      ))}
    </select>
  );
}

function ColorSwatch({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="color"
        value={value || "#3b82f6"}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-9 rounded border border-border bg-transparent p-0 cursor-pointer"
        aria-label="Pick color"
        data-testid="mapdesign-color-input"
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#hex"
        className="input text-xs w-20 mono"
      />
    </div>
  );
}

function PreviewSwatch({ color, iconKey, size = 20 }) {
  const IconCmp = MAP_ICON_LIBRARY.find((i) => i.key === iconKey)?.Icon || Circle;
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size,
        backgroundColor: color || "#3b82f6",
        boxShadow: "0 0 0 2px #fff, 0 0 0 3px rgba(0,0,0,0.2)",
      }}
      aria-hidden="true"
    >
      <IconCmp size={Math.round(size * 0.55)} weight="fill" color="#fff" />
    </div>
  );
}

/**
 * Renders one editable row for a taxonomy entity (a status, priority, or tag).
 */
function TaxonomyRow({ id, label, value, onChange }) {
  const color = value?.color || "";
  const icon = value?.icon || "circle";
  return (
    <div
      className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2 border-b border-border last:border-b-0"
      data-testid={`mapdesign-row-${id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <PreviewSwatch color={color} iconKey={icon} />
        <span className="text-sm truncate">{label}</span>
      </div>
      <ColorSwatch
        value={color}
        onChange={(c) => onChange({ ...value, color: c })}
      />
      <IconPicker
        value={icon}
        onChange={(k) => onChange({ ...value, icon: k })}
      />
      {(color || icon !== "circle") && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-muted-foreground hover:text-destructive"
          title="Reset this row"
          data-testid={`mapdesign-reset-${id}`}
        >Reset</button>
      )}
    </div>
  );
}

export default function MapDesignSection() {
  const { user } = useAuth();
  // Super_admin or admin. Both can save. Non-admins never see this
  // section (parent Settings tab already gates on admin).
  const canEdit = user?.role === "admin" || user?.role === "super_admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [tags, setTags] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ms, st, pr, tg] = await Promise.all([
        api.get("/command-center/map-settings").then((r) => r.data).catch(() => ({})),
        api.get("/jobs/statuses").then((r) => r.data).catch(() => []),
        api.get("/locations/priorities").then((r) => r.data).catch(() => []),
        api.get("/tags?entity_type=location").then((r) => r.data).catch(() => []),
      ]);
      setSettings(ms && typeof ms === "object" ? ms : {});
      setStatuses(Array.isArray(st) ? st : []);
      setPriorities(Array.isArray(pr) ? pr : []);
      setTags(Array.isArray(tg) ? tg : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setGroupRow = (group, id, next) => {
    setSettings((cur) => {
      const nextGroup = { ...(cur[group] || {}) };
      if (next == null) delete nextGroup[id];
      else nextGroup[id] = next;
      return { ...cur, [group]: nextGroup };
    });
  };
  const setSingle = (key, next) => {
    setSettings((cur) => ({ ...cur, [key]: next }));
  };

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await api.put("/command-center/map-settings", settings);
      toast.success("Map design saved. Command Center will pick this up on next load.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const applyPalette = () => {
    // Auto-assign the default palette across statuses that have no color yet.
    setSettings((cur) => {
      const next = { ...cur, statuses: { ...(cur.statuses || {}) } };
      let idx = 0;
      for (const s of statuses) {
        if (!next.statuses[s.id]?.color) {
          next.statuses[s.id] = {
            ...(next.statuses[s.id] || {}),
            color: DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length],
            icon: next.statuses[s.id]?.icon || "circle",
          };
          idx++;
        }
      }
      return next;
    });
    toast("Palette auto-applied. Review and save.", { icon: "🎨" });
  };

  const totalCustomized = useMemo(() => {
    let n = 0;
    for (const grp of ["statuses", "priorities", "tags"]) {
      if (settings?.[grp]) n += Object.keys(settings[grp]).length;
    }
    return n;
  }, [settings]);

  if (loading) return <div className="p-4 text-xs text-muted-foreground">Loading map design…</div>;

  return (
    <div className="space-y-6" data-testid="mapdesign-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-xs mb-1 flex items-center gap-2">
            <Palette size={12} /> MAP DESIGN
          </div>
          <div className="text-sm text-muted-foreground max-w-2xl">
            Assign brand colors and glyphs to the taxonomy that drives your
            Command Center map. Statuses, priorities, and location tags each
            get their own override — the mobile driver app intentionally
            ignores these and uses a fixed high-contrast safety palette.
          </div>
          {totalCustomized > 0 && (
            <div className="mt-1 text-xs mono text-muted-foreground" data-testid="mapdesign-count">
              {totalCustomized} custom mapping{totalCustomized === 1 ? "" : "s"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={applyPalette}
            className="btn btn-ghost btn-sm"
            title="Auto-assign the default palette to un-colored statuses"
            data-testid="mapdesign-apply-palette"
          >
            <ArrowsClockwise size={12} className="mr-1" /> Auto-palette
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !canEdit}
            className="btn btn-primary btn-sm disabled:opacity-50"
            data-testid="mapdesign-save"
          >
            <FloppyDisk size={12} className="mr-1" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Universal fallbacks */}
      <div className="surface p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border label-xs">DEFAULTS</div>
        <TaxonomyRow
          id="default"
          label="Default pin (uncategorized jobs)"
          value={settings.default || {}}
          onChange={(v) => setSingle("default", v)}
        />
        <TaxonomyRow
          id="meeting"
          label="Setup meeting override (is_setup_meeting = true)"
          value={settings.meeting || {}}
          onChange={(v) => setSingle("meeting", v)}
        />
      </div>

      {/* Statuses */}
      <div className="surface p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border label-xs flex items-center justify-between">
          <span>JOB STATUSES ({statuses.length})</span>
          <span className="text-muted-foreground text-[10px] normal-case">Overrides apply to every job in that status</span>
        </div>
        {statuses.length === 0 && (
          <div className="text-xs text-muted-foreground italic p-4">No job statuses configured yet.</div>
        )}
        {statuses.map((s) => (
          <TaxonomyRow
            key={s.id}
            id={s.id}
            label={s.name}
            value={settings.statuses?.[s.id] || {}}
            onChange={(v) => setGroupRow("statuses", s.id, v)}
          />
        ))}
      </div>

      {/* Priorities */}
      <div className="surface p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border label-xs flex items-center justify-between">
          <span>LOCATION PRIORITIES ({priorities.length})</span>
          <span className="text-muted-foreground text-[10px] normal-case">Overrides show when no status color is set</span>
        </div>
        {priorities.length === 0 && (
          <div className="text-xs text-muted-foreground italic p-4">No priorities configured.</div>
        )}
        {priorities.map((p) => (
          <TaxonomyRow
            key={p.id}
            id={p.id}
            label={p.name}
            value={settings.priorities?.[p.id] || {}}
            onChange={(v) => setGroupRow("priorities", p.id, v)}
          />
        ))}
      </div>

      {/* Location tags */}
      <div className="surface p-0 overflow-hidden">
        <div className="px-3 py-2 border-b border-border label-xs flex items-center justify-between">
          <span>LOCATION TAGS ({tags.length})</span>
          <span className="text-muted-foreground text-[10px] normal-case">Used for filtering in the map lasso &amp; filters</span>
        </div>
        {tags.length === 0 && (
          <div className="text-xs text-muted-foreground italic p-4">No tags yet. Create tags from any Location detail page.</div>
        )}
        {tags.map((t) => (
          <TaxonomyRow
            key={t.id}
            id={t.id}
            label={t.name}
            value={settings.tags?.[t.id] || {}}
            onChange={(v) => setGroupRow("tags", t.id, v)}
          />
        ))}
      </div>
    </div>
  );
}
